import React, { useEffect, useMemo, useRef, useState } from "react";
import config from "../../config";
import "./style.scss";
import PriceSegmentTable from "./priceSegmentTable";
import axios from "axios";
import { FaCheck, FaDownload, FaFilter, FaSyncAlt, FaTimes } from "react-icons/fa";

const backendUrl = config.backend_url;

const DEALER_FILTER_TYPES = [
  { key: "zone", label: "Zone" },
  { key: "district", label: "District" },
  { key: "town", label: "Town" },
  { key: "category", label: "Category" },
  { key: "labels", label: "Label" },
  { key: "top_outlet", label: "Top Outlet" },
];

const ACTOR_POSITION_KEYS = ["smd", "zsm", "asm", "mdd", "tse", "so", "dealer"];
const FLOW_NAME = "default_sales_flow";

const formatDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDatePreset = (preset) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (preset === "today") {
    const value = formatDateInputValue(today);
    return { start: value, end: value };
  }

  if (preset === "yesterday") {
    const value = formatDateInputValue(yesterday);
    return { start: value, end: value };
  }

  if (preset === "this_month") {
    return {
      start: formatDateInputValue(new Date(today.getFullYear(), today.getMonth(), 1)),
      end: formatDateInputValue(today),
    };
  }

  if (preset === "last_month") {
    return {
      start: formatDateInputValue(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      end: formatDateInputValue(new Date(today.getFullYear(), today.getMonth(), 0)),
    };
  }

  return { start: "", end: "" };
};

const DETAIL_ENDPOINT_CANDIDATES = [
  "/reports/dashboard-summary/drilldown",
  "/reports/dashboard-summary/details",
  "/reports/dashboard-summary-detail",
  "/reports/dashboard-summary",
];

const MONTH_SHORT_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const EXPECTED_ACHIEVEMENT_LABEL = "%Exp Ach";

const isExpectedAchievementColumn = (column) =>
  ["wfm", "wmf"].includes(String(column || "").trim().toLowerCase());

const isExpAchievementColumn = (column) =>
  ["expach", "exp.ach"].includes(String(column || "").trim().toLowerCase());

const isPercentMetricColumn = (column) =>
  String(column || "").includes("%") || isExpectedAchievementColumn(column);

const isGrowthColumn = (column) => String(column || "").trim() === "G/D%";

const getGrowthTextClass = (value, column = "G/D%") => {
  if (!isGrowthColumn(column)) return "";
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "neutral-growth";
  return num > 0 ? "positive" : "negative";
};

const formatSalesReportColumnLabel = (column) => {
  if (isExpectedAchievementColumn(column)) return EXPECTED_ACHIEVEMENT_LABEL;
  if (isExpAchievementColumn(column)) return "Exp Ach";
  if (String(column || "").trim() === "DOS") return "Stock Depth";
  return column;
};

const formatWodColumnLabel = (column) => {
  if (isExpectedAchievementColumn(column)) return EXPECTED_ACHIEVEMENT_LABEL;

  const match = String(column || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return column;

  const year = match[1].slice(-2);
  const monthIndex = Number(match[2]) - 1;
  const monthLabel = MONTH_SHORT_LABELS[monthIndex];

  return monthLabel ? `${monthLabel}'${year}` : column;
};

const getWodTagCellValue = (row, column) => {
  if (Object.prototype.hasOwnProperty.call(row, column)) return row[column];

  const match = String(column || "").match(/^\d{4}-(\d{2})$/);
  if (!match) return row[column];

  const monthIndex = Number(match[1]) - 1;
  const monthLabel = MONTH_SHORT_LABELS[monthIndex];
  return monthLabel ? row[monthLabel] : row[column];
};

const ShimmerBlock = ({ height = 14, width = "100%", style = {} }) => (
  <div
    className="shimmer"
    style={{
      height,
      width,
      borderRadius: 6,
      ...style,
    }}
  />
);

const SectionLoader = ({ title, tone = "blue" }) => (
  <div className={`report-card report-card--${tone}`}>
    <div className="report-card__header">
      <div>
        <h3>{title}</h3>
        <p>Loading report data...</p>
      </div>
      <span className="report-card__badge">REPORT</span>
    </div>

    <div className="report-card__content">
      <div className="skeleton-box">
        <div className="skeleton-title">
          <ShimmerBlock height={22} width="220px" />
        </div>

        <div className="skeleton-cards">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton-card">
              <div className="skeleton-card-label">
                <ShimmerBlock height={12} width="45%" />
              </div>
              <ShimmerBlock height={22} width={`${55 + i * 8}%`} />
            </div>
          ))}
        </div>

        <div className="skeleton-rows">
          <div className="skeleton-row">
            <ShimmerBlock height={18} width="70%" />
            <ShimmerBlock height={18} width="55%" />
            <ShimmerBlock height={18} width="85%" />
            <ShimmerBlock height={18} width="60%" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ReportGroup = ({
  title,
  subtitle,
  tone = "blue",
  children,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className={`report-group report-group--${tone}`}>
      <div className="report-group__header" onClick={() => setOpen(!open)}>
        <div>
          <h2>{title}</h2>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <span className="report-group__toggle">{open ? "−" : "+"}</span>
      </div>

      {open && <div className="report-group__body">{children}</div>}
    </section>
  );
};

const ReportCard = ({ title, subtitle, tone = "blue", children }) => (
  <div className={`report-card report-card--${tone}`}>
    <div className="report-card__header">
      <div>
        <h3>{title}</h3>
        {subtitle && <p>{subtitle}</p>}
      </div>
      <span className="report-card__badge">REPORT</span>
    </div>

    <div className="report-card__content">{children}</div>
  </div>
);

const FilterChip = ({ children, onClick }) => (
  <button type="button" className="sales-filter-chip" onClick={onClick}>
    <span>{children}</span>
    <FaTimes />
  </button>
);

const OptionShimmerGrid = ({ count = 6 }) => (
  <div className="sales-option-grid sales-option-grid--loading">
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className="sales-option-pill sales-option-pill--shimmer">
        <div className="sales-option-pill__shimmer-line sales-option-pill__shimmer-line--title" />
        <div className="sales-option-pill__shimmer-line sales-option-pill__shimmer-line--meta" />
      </div>
    ))}
  </div>
);

const isActorTab = (tabKey) => ACTOR_POSITION_KEYS.includes(tabKey);

const DETAIL_COLUMN_EXCLUDE = new Set([
  "Product Code",
  "Model Code",
  "Product DB Status",
]);

const isMonthMetricColumn = (column) =>
  MONTH_SHORT_LABELS.includes(String(column || "").trim()) ||
  /^\d{4}-\d{2}$/.test(String(column || "").trim());

const parseNumericValue = (value) => {
  if (typeof value === "number") return value;
  const raw = String(value ?? "")
    .replace(/₹/g, "")
    .replace(/,/g, "")
    .trim()
    .toLowerCase();
  const parsed = Number(raw.replace(/cr|lac|lakh|k|%/g, "").trim());
  if (!Number.isFinite(parsed)) return NaN;
  if (raw.includes("cr")) return parsed * 10000000;
  if (raw.includes("lac") || raw.includes("lakh")) return parsed * 100000;
  if (raw.includes("k")) return parsed * 1000;
  return parsed;
};

const toDetailNumber = (value) => {
  const parsed = parseNumericValue(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const filterDetailColumns = (columns = []) =>
  columns.filter((column) => !DETAIL_COLUMN_EXCLUDE.has(String(column || "").trim()));

const getDetailSortAverage = (row, columns = []) => {
  if (columns.includes("Average Stock")) {
    return toDetailNumber(row["Average Stock"]);
  }
  const monthColumns = columns.filter(isMonthMetricColumn);
  if (!monthColumns.length) return 0;

  const total = monthColumns.reduce((sum, column) => sum + toDetailNumber(row[column]), 0);
  return total / monthColumns.length;
};

const normalizeFilterOption = (item) => {
  if (!item) return null;
  if (typeof item === "string") return { label: item, value: item };

  const value = item.value ?? item.tag ?? item.name ?? item.label;
  if (!value) return null;

  return {
    ...item,
    label: item.label || item.name || item.tag || item.value,
    value,
  };
};

const normalizeProductDetailRows = (rawRows = [], metricColumns = []) =>
  rawRows
    .map((row) => {
      if (!row || typeof row !== "object") return null;

      const productComposite = String(
        row.Product || row.product || row.productLabel || row.product_name || row.name || ""
      ).trim();
      const [compositeModelCode, ...compositeNameParts] = productComposite.split(" - ");

      const normalized = {
        productLabel:
          compositeNameParts.join(" - ").trim() ||
          row.productLabel ||
          row.product_name ||
          row.productName ||
          row.name ||
          row.description ||
          row.model_name ||
          "-",
        modelCode:
          compositeModelCode ||
          row.model_code ||
          row.modelCode ||
          row.model ||
          row.product_code ||
          row.productCode ||
          "-",
      };

      metricColumns.forEach((column) => {
        normalized[column] = row[column];
      });

      return normalized;
    })
    .filter(Boolean)
    .sort((a, b) => {
      const avgDiff = getDetailSortAverage(b, metricColumns) - getDetailSortAverage(a, metricColumns);
      if (avgDiff !== 0) return avgDiff;
      return String(a.productLabel || "").localeCompare(String(b.productLabel || ""));
    });

const normalizeGroupedTagRows = (groupedReport) => {
  if (!groupedReport?.columns || !Array.isArray(groupedReport?.rows)) {
    return { rowLabel: "Tag", metricColumns: [], rows: [] };
  }

  const [rowLabel = "Tag", ...metricColumns] = groupedReport.columns;
  const rows = groupedReport.rows.filter(
    (row) => row && row[rowLabel] && String(row[rowLabel]).toLowerCase() !== "total"
  );

  return { rowLabel, metricColumns, rows };
};

function SalesReportV2() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [compactMode, setCompactMode] = useState(true);
  const [actorPositions, setActorPositions] = useState([]);

  const [filterValues, setFilterValues] = useState({
    zone: [],
    district: [],
    town: [],
    category: [],
    labels: [],
    top_outlet: [],
  });

  const [actorOptionsMap, setActorOptionsMap] = useState({});
  const [selectedActorFilters, setSelectedActorFilters] = useState({});
  const [selectedDealerFilters, setSelectedDealerFilters] = useState({
    zone: [],
    district: [],
    town: [],
    category: [],
    labels: [],
    top_outlet: [],
  });

  const [brand, setBrand] = useState("");
  const [segment, setSegment] = useState("");

  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [activeFilterTab, setActiveFilterTab] = useState("zone");
  const [searchText, setSearchText] = useState("");
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);

  const [detailModal, setDetailModal] = useState({
    open: false,
    title: "",
    subtitle: "",
    columns: [],
    rows: [],
    loading: false,
    error: "",
  });

  const defaultDealerFilterValues = useMemo(
    () => ({
      zone: [],
      district: [],
      town: [],
      category: [],
      labels: [],
      top_outlet: [],
    }),
    []
  );

  const panelRef = useRef(null);

  const authHeaders = useMemo(
    () => ({
      Authorization: localStorage.getItem("authToken"),
    }),
    []
  );

  const applyDatePreset = (preset) => {
    const nextRange = getDatePreset(preset);
    setStartDate(nextRange.start);
    setEndDate(nextRange.end);
  };

  const handleStartDateChange = (value) => {
    setStartDate(value);
    if (value && endDate && value > endDate) {
      setEndDate(value);
    }
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    if (value && startDate && value < startDate) {
      setStartDate(value);
    }
  };

  const actorPositionOrder = useMemo(
    () => actorPositions.map((item) => item.value).filter(Boolean),
    [actorPositions]
  );

  const [activation, setActivation] = useState(null);
  const [tertiary, setTertiary] = useState(null);
  const [secondary, setSecondary] = useState(null);
  const [wodTables, setWodTables] = useState(null);
  const [stockWod, setStockWod] = useState(null);
  const [priceSegmentTables, setPriceSegmentTables] = useState(null);
  const [priceSegmentSplit40k, setPriceSegmentSplit40k] = useState(null);
  const [tagGroupedReports, setTagGroupedReports] = useState({});

  const [activationYtdMatrix, setActivationYtdMatrix] = useState(null);

  const [loadingActivation, setLoadingActivation] = useState(false);
  const [loadingTertiary, setLoadingTertiary] = useState(false);
  const [loadingSecondary, setLoadingSecondary] = useState(false);
  const [loadingWod, setLoadingWod] = useState(false);
  const [loadingStockWod, setLoadingStockWod] = useState(false);
  const [loadingPriceSegment, setLoadingPriceSegment] = useState(false);
  const [loadingPriceSegmentSplit40k, setLoadingPriceSegmentSplit40k] =
    useState(false);

  const [loadingActivationYtdMatrix, setLoadingActivationYtdMatrix] =
    useState(false);

  const [loadingCoreTagRows, setLoadingCoreTagRows] = useState(false);

  const formatCompact = (num, isCurrency = false) => {
    if (num === null || num === undefined) return "-";

    const n = parseNumericValue(num);
    if (Number.isNaN(n)) return "-";
    const isNegative = n < 0;
    const abs = Math.abs(n);

    let formatted;

    if (isCurrency) {
      formatted = (abs / 10000000).toFixed(2).replace(/\.00$/, "") + " Cr";
    } else if (abs > 0) {
      formatted = (abs / 1000).toFixed(2).replace(/\.00$/, "") + " K";
    } else {
      formatted = "0 K";
    }

    if (isCurrency) return (isNegative ? "-₹ " : "₹ ") + formatted;
    return isNegative ? "-" + formatted : formatted;
  };

  const formatNormal = (num, isCurrency = false) => {
    if (num === null || num === undefined) return "-";

    const n = parseNumericValue(num);
    if (Number.isNaN(n)) return "-";
    const formatted = Math.abs(n).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });

    if (isCurrency) return n < 0 ? `-₹ ${formatted}` : `₹ ${formatted}`;
    return n < 0 ? `-${formatted}` : formatted;
  };

  const formatValue = (num, isCurrency = false) =>
    compactMode ? formatCompact(num, isCurrency) : formatNormal(num, isCurrency);

  const isPlainRoundedColumn = (column) => {
    const key = String(column || "").trim().toLowerCase();
    return key === "days of stock" || key === "dos";
  };

  const formatPlainRounded = (num) => {
    if (num === null || num === undefined) return "-";
    if (String(num).trim() === "" || String(num).trim() === "-") return "-";
    const n = parseNumericValue(num);
    if (Number.isNaN(n)) return "-";
    return Math.round(n).toLocaleString("en-IN");
  };

  const formatReportCell = (value, column, isCurrency = false) => {
    if (isPlainRoundedColumn(column)) return formatPlainRounded(value);
    if (isPercentMetricColumn(column)) return formatPercent(value);
    return formatValue(value, isCurrency);
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "-";
    return `${Number(num).toFixed(2)}%`;
  };

  const isTextDetailColumn = (column) =>
    ["Product Code", "Model Code", "Product DB Status"].includes(String(column || ""));

  const formatDetailCell = (value, column) => {
    if (isTextDetailColumn(column)) return value || "-";
    if (["SPD Stk WOD", "MDD Stk WOD", "Ret Stk WOD", "Total Stock WOD", "Average Stock"].includes(column)) {
      return formatPlainRounded(value);
    }
    return formatReportCell(value, column, false);
  };

  const escapeCsvValue = (value) => {
    const stringValue = value === null || value === undefined ? "" : String(value);
    return /[",\n]/.test(stringValue)
      ? `"${stringValue.replace(/"/g, '""')}"`
      : stringValue;
  };

  const downloadDetailModalCsv = () => {
    if (!detailModal.rows.length) return;

    const headers = ["Product Name / Model Code", ...detailModal.columns.map(formatSalesReportColumnLabel)];
    const csvRows = detailModal.rows.map((row) => [
      `${row.productLabel || ""}${row.modelCode ? ` - ${row.modelCode}` : ""}`.trim(),
      ...detailModal.columns.map((column) => row[column]),
    ]);

    const csv = [headers, ...csvRows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const fileLabel = `${detailModal.title || "product-details"}-${detailModal.subtitle || "report"}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

    link.href = url;
    link.download = `${fileLabel || "product-details"}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const buildSubordinateFilters = (
    source = selectedActorFilters,
    { upToPosition = null } = {}
  ) => {
    const filters = {};
    const orderedPositions = actorPositionOrder.length
      ? actorPositionOrder
      : ACTOR_POSITION_KEYS;

    for (const position of orderedPositions) {
      if (upToPosition && position === upToPosition) break;

      const codes = (source[position] || [])
        .map((item) => item.code)
        .filter(Boolean);

      if (codes.length) {
        filters[position] = codes;
      }
    }

    return filters;
  };

  const buildDealerFiltersPayload = (
    source = selectedDealerFilters,
    { excludeType = null } = {}
  ) => {
    const filters = {};

    Object.entries(source).forEach(([key, selected]) => {
      if (key === excludeType || !selected?.length) return;

      const values = selected
        .map((item) => item.value)
        .filter((value) => value !== undefined && value !== null && value !== "");

      if (values.length) {
        filters[key] = values;
      }
    });

    return filters;
  };

  const fetchDropdownOptions = async ({
    targetType,
    targetKey,
    subordinates = {},
    dealer = {},
  }) => {
    try {
      const body = {
        flow_name: FLOW_NAME,
        target_type: targetType,
        target_key: targetKey,
        subordinates,
        dealer,
        product_tags: {},
      };

      const res = await axios.post(
        `${backendUrl}/filters/dropdown-options`,
        body,
        { headers: authHeaders }
      );

      return res.data?.values || [];
    } catch (error) {
      console.error(
        `Error fetching dropdown options for ${targetType}/${targetKey}:`,
        error
      );
      return [];
    }
  };

  const fetchGroupingOptions = async () => {
    try {
      const res = await axios.get(`${backendUrl}/grouping-options`, {
        headers: authHeaders,
      });

      setActorPositions(res.data.actorPositions || []);
    } catch (error) {
      console.error("Error fetching sales filter metadata:", error);
    }
  };

  const loadFilterOptionsForTab = async (tabKey) => {
    if (!tabKey) return;

    setLoadingFilterOptions(true);

    try {
      if (isActorTab(tabKey)) {
        const values = await fetchDropdownOptions({
          targetType: "subordinate",
          targetKey: tabKey,
          subordinates: buildSubordinateFilters(selectedActorFilters, {
            upToPosition: tabKey,
          }),
          dealer: buildDealerFiltersPayload(),
        });

        setActorOptionsMap((old) => ({
          ...old,
          [tabKey]: values,
        }));
        return;
      }

      const values = await fetchDropdownOptions({
        targetType: "dealer",
        targetKey: tabKey,
        subordinates: buildSubordinateFilters(),
        dealer: buildDealerFiltersPayload(selectedDealerFilters, {
          excludeType: tabKey,
        }),
      });

      setFilterValues((old) => ({
        ...old,
        [tabKey]: values,
      }));
    } finally {
      setLoadingFilterOptions(false);
    }
  };

  const totalSelectedFiltersCount = useMemo(() => {
    const actorCount = Object.values(selectedActorFilters).reduce(
      (sum, arr) => sum + (arr?.length || 0),
      0
    );
    const dealerCount = Object.values(selectedDealerFilters).reduce(
      (sum, arr) => sum + (arr?.length || 0),
      0
    );
    return actorCount + dealerCount;
  }, [selectedActorFilters, selectedDealerFilters]);

  const currentTabOptions = useMemo(() => {
    if (isActorTab(activeFilterTab)) {
      return actorOptionsMap[activeFilterTab] || [];
    }
    return filterValues[activeFilterTab] || [];
  }, [activeFilterTab, actorOptionsMap, filterValues]);

  const currentTabSelected = useMemo(() => {
    if (isActorTab(activeFilterTab)) {
      return selectedActorFilters[activeFilterTab] || [];
    }
    return selectedDealerFilters[activeFilterTab] || [];
  }, [activeFilterTab, selectedActorFilters, selectedDealerFilters]);

  const filteredCurrentOptions = useMemo(() => {
    const selected = currentTabSelected || [];
    const selectedKeys = new Set(
      selected.map((item) =>
        isActorTab(activeFilterTab) ? item.code : String(item.value)
      )
    );
    const mergedOptions = [
      ...selected,
      ...currentTabOptions.filter((item) => {
        const key = isActorTab(activeFilterTab) ? item.code : String(item.value);
        return !selectedKeys.has(key);
      }),
    ];
    const q = searchText.trim().toLowerCase();
    if (!q) return mergedOptions;

    return mergedOptions.filter((item) => {
      const raw =
        `${item.label || ""} ${item.name || ""} ${item.code || ""} ${item.value || ""}`.toLowerCase();
      return raw.includes(q);
    });
  }, [activeFilterTab, currentTabOptions, currentTabSelected, searchText]);

  const toggleSelection = (type, item) => {
    if (isActorTab(type)) {
      const orderedPositions = actorPositionOrder.length
        ? actorPositionOrder
        : ACTOR_POSITION_KEYS;
      const positionIndex = orderedPositions.indexOf(type);

      setSelectedActorFilters((old) => {
        const prev = old[type] || [];
        const exists = prev.some((x) => x.code === item.code);

        const next = {
          ...old,
          [type]: exists ? prev.filter((x) => x.code !== item.code) : [...prev, item],
        };

        if (positionIndex !== -1) {
          orderedPositions.slice(positionIndex + 1).forEach((position) => {
            next[position] = [];
          });
        }

        return next;
      });

      setActorOptionsMap((old) => {
        const next = { ...old };
        if (positionIndex !== -1) {
          orderedPositions.slice(positionIndex + 1).forEach((position) => {
            delete next[position];
          });
        }
        return next;
      });

      return;
    }

    const prev = selectedDealerFilters[type] || [];
    const exists = prev.some((x) => x.value === item.value);

    setSelectedDealerFilters((old) => ({
      ...old,
      [type]:
        type === "top_outlet"
          ? exists
            ? []
            : [item]
          : exists
          ? prev.filter((x) => x.value !== item.value)
          : [...prev, item],
    }));

    setFilterValues((old) => ({
      ...defaultDealerFilterValues,
      [type]: old[type] || [],
    }));
  };

  const removeSelection = (type, item) => {
    if (isActorTab(type)) {
      setSelectedActorFilters((old) => ({
        ...old,
        [type]: (old[type] || []).filter((x) => x.code !== item.code),
      }));
      return;
    }

    setSelectedDealerFilters((old) => ({
      ...old,
      [type]: (old[type] || []).filter((x) => x.value !== item.value),
    }));
  };

  const clearCurrentTab = () => {
    if (isActorTab(activeFilterTab)) {
      setSelectedActorFilters((old) => ({
        ...old,
        [activeFilterTab]: [],
      }));
      return;
    }

    setSelectedDealerFilters((old) => ({
      ...old,
      [activeFilterTab]: [],
    }));
  };

  const resetAllFilters = () => {
    setStartDate("");
    setEndDate("");
    setBrand("");
    setSegment("");
    setSelectedActorFilters({});
    setSelectedDealerFilters(defaultDealerFilterValues);
    setActorOptionsMap({});
    setFilterValues(defaultDealerFilterValues);
    setSearchText("");
    setActiveFilterTab(actorPositions[0]?.value || "zone");
    setFilterPanelOpen(false);
  };

  const renderChips = (type) => {
    const selected = isActorTab(type)
      ? selectedActorFilters[type] || []
      : selectedDealerFilters[type] || [];

    if (!selected.length) return null;

    return (
      <div className="sales-filter-chip-list">
        {selected.map((item) => (
          <FilterChip
            key={item.code || item.value}
            onClick={() => removeSelection(type, item)}
          >
            {item.label || item.name || item.value}
          </FilterChip>
        ))}
      </div>
    );
  };

  const closeDetailModal = () => {
    setDetailModal({
      open: false,
      title: "",
      subtitle: "",
      columns: [],
      rows: [],
      loading: false,
      error: "",
    });
  };

  const resolveDetailResponse = (result, metricColumns = []) => {
    const productIdentityColumns = new Set([
      "Product",
      "product",
      "product_name",
      "productName",
      "name",
      "model_code",
      "modelCode",
      "model",
      "product_code",
      "productCode",
    ]);

    const payload =
      result?.data ||
      result?.details ||
      result?.drilldown ||
      result?.products ||
      result?.productRows ||
      result?.product_rows ||
      result;

    if (Array.isArray(payload)) {
      const columns = filterDetailColumns(metricColumns);
      return {
        columns,
        rows: normalizeProductDetailRows(payload, columns),
      };
    }

    if (payload?.headers && Array.isArray(payload?.data)) {
      const columns = filterDetailColumns(
        payload.headers.filter((header) => !productIdentityColumns.has(header))
      );
      return {
        columns,
        rows: normalizeProductDetailRows(payload.data, columns),
      };
    }

    if (payload?.columns && Array.isArray(payload?.rows)) {
      const columns = filterDetailColumns(
        payload.columns.filter((column) => !productIdentityColumns.has(column))
      );
      return {
        columns,
        rows: normalizeProductDetailRows(payload.rows, columns),
      };
    }

    if (Array.isArray(payload?.products)) {
      const columns = filterDetailColumns(metricColumns);
      return {
        columns,
        rows: normalizeProductDetailRows(payload.products, columns),
      };
    }

    return { columns: [], rows: [] };
  };

  const getRequestBody = (report_type, extras = {}) => {
    const body = {
      flow_name: FLOW_NAME,
      filters: {
        report_type,
        ...(brand ? { brand } : {}),
        ...(segment ? { segment } : {}),
      },
      subordinate_filters: buildSubordinateFilters(),
      dealer_filters: buildDealerFiltersPayload(),
      ...(brand ? { brand } : {}),
      ...(segment ? { segment } : {}),
    };

    if (startDate && endDate) {
      body.start_date = startDate;
      body.end_date = endDate;
    }

    const mergedFilters = {
      ...body.filters,
      ...(extras.filters || {}),
    };

    return {
      ...body,
      ...extras,
      filters: mergedFilters,
    };
  };

  const openTagDetailModal = async ({ reportType, reportTitle, row, metricColumns = [] }) => {
    const sourceKey = row.__sourceKey || "";

    setDetailModal({
      open: true,
      title: `${row.__tagLabel} Products`,
      subtitle: reportTitle,
      columns: metricColumns,
      rows: [],
      loading: true,
      error: "",
    });

    for (const endpoint of DETAIL_ENDPOINT_CANDIDATES) {
      try {
        const res = await fetch(`${backendUrl}${endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("authToken"),
          },
          body: JSON.stringify(
            getRequestBody(reportType, {
              filters: {
                group_by: "tag",
              },
              drilldown: {
                group_value: row.__tagLabel,
                source_key: sourceKey,
              },
            })
          ),
        });

        const result = await res.json();
        if (!res.ok) {
          throw new Error(result.message || "Failed to fetch product details");
        }

        const resolved = resolveDetailResponse(result, metricColumns);
        if (resolved.rows.length) {
          setDetailModal((old) => ({
            ...old,
            columns: resolved.columns.length ? resolved.columns : metricColumns,
            rows: resolved.rows,
            loading: false,
            error: "",
          }));
          return;
        }
      } catch (error) {
        continue;
      }
    }

    setDetailModal((old) => ({
      ...old,
      loading: false,
      error: "Product details are not available yet for this row.",
    }));
  };

  const fetchCoreReports = async () => {
    setLoadingActivation(true);
    setLoadingTertiary(true);
    setLoadingSecondary(true);
    setLoadingWod(true);
    setLoadingStockWod(true);
    setLoadingPriceSegment(true);
    setLoadingPriceSegmentSplit40k(true);
    setTagGroupedReports((old) => {
      const { activation, tertiary, secondary, wod, ...rest } = old;
      return rest;
    });

    try {
      const res = await fetch(`${backendUrl}/reports/dashboard-summary-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("authToken"),
        },
        body: JSON.stringify({
          ...getRequestBody("batch"),
          include_tag_grouped: false,
          report_types: [
            "activation",
            "tertiary",
            "secondary",
            "wod",
            "stock_wod",
          ],
        }),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Core fetch failed");

      const data = result.data || {};

      setActivation(data.activation || null);
      setTertiary(data.tertiary || null);
      setSecondary(data.secondary || null);
      setWodTables(data.wod || null);
      setStockWod(data.stock_wod || null);

      setTagGroupedReports((old) => ({
        ...old,
        ...(data.tag_grouped || {}),
      }));

      setTimeout(() => {
        fetchCoreTagGroupedReports();
      }, 0);
    } catch (error) {
      console.error("Core fetch error:", error);
    } finally {
      setLoadingActivation(false);
      setLoadingTertiary(false);
      setLoadingSecondary(false);
      setLoadingWod(false);
      setLoadingStockWod(false);
    }
  };

  const fetchCoreTagGroupedReports = async () => {
    setLoadingCoreTagRows(true);

    try {
      const res = await fetch(`${backendUrl}/reports/dashboard-summary-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("authToken"),
        },
        body: JSON.stringify({
          ...getRequestBody("batch"),
          include_tag_grouped: true,
          only_tag_grouped: true,
          report_types: [
            "activation",
            "tertiary",
            "secondary",
            "wod",
          ],
        }),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Core tag fetch failed");

      const data = result.data || {};

      setTagGroupedReports((old) => ({
        ...old,
        ...(data.tag_grouped || {}),
      }));
    } catch (error) {
      console.error("Core tag-grouped fetch error:", error);
    } finally {
      setLoadingCoreTagRows(false);
    }
  };

  const fetchYtdReports = async () => {
    setLoadingActivationYtdMatrix(true);

    try {
      const res = await fetch(`${backendUrl}/reports/dashboard-summary-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("authToken"),
        },
        body: JSON.stringify({
          ...getRequestBody("batch"),
          include_tag_grouped: false, // 🚀 fast initial load
          report_types: [
            "activation_ytd_matrix",
          ],
        }),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "YTD fetch failed");

      const data = result.data || {};

      setActivationYtdMatrix(data.activation_ytd_matrix || null);

    } catch (error) {
      console.error("YTD fetch error:", error);
    } finally {
      setLoadingActivationYtdMatrix(false);
    }
  };

  const fetchPriceSegmentReports = async () => {
    try {
      const res = await fetch(`${backendUrl}/reports/dashboard-summary-batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("authToken"),
        },
        body: JSON.stringify({
          ...getRequestBody("batch"),
          include_tag_grouped: false,
          report_types: [
            "price_segment",
            "price_segment_40k",
          ],
        }),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Price segment fetch failed");

      const data = result.data || {};

      setPriceSegmentTables(data.price_segment || null);
      setPriceSegmentSplit40k(data.price_segment_40k || null);

      setTagGroupedReports((old) => ({
        ...old,
        ...(data.tag_grouped || {}),
      }));
    } catch (error) {
      console.error("Price segment fetch error:", error);
    } finally {
      setLoadingPriceSegment(false);
      setLoadingPriceSegmentSplit40k(false);
    }
  };

  useEffect(() => {
    fetchGroupingOptions();
  }, []);

  useEffect(() => {
    if (!actorPositions.length) return;

    const actorTabSet = new Set(actorPositions.map((item) => item.value));
    const availableStaticTabs = [...DEALER_FILTER_TYPES];

    if (
      !actorTabSet.has(activeFilterTab) &&
      !availableStaticTabs.some((item) => item.key === activeFilterTab)
    ) {
      setActiveFilterTab(actorPositions[0]?.value || "zone");
    }
  }, [actorPositions, activeFilterTab]);

  useEffect(() => {
    if (!filterPanelOpen || !activeFilterTab) return;
    loadFilterOptionsForTab(activeFilterTab);
  }, [filterPanelOpen, activeFilterTab]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (filterPanelOpen && panelRef.current && !panelRef.current.contains(event.target)) {
        setFilterPanelOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [filterPanelOpen]);

  useEffect(() => {
    if (!actorPositions.length) return;
    fetchCoreReports();
    fetchYtdReports();
    fetchPriceSegmentReports();
  }, [actorPositions]);

  useEffect(() => {
    if (!filterPanelOpen) return;

    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, [filterPanelOpen]);

  const renderInlineTagRows = (
    groupedReport,
    {
      reportType,
      reportTitle,
      metricColumns = [],
      firstColumnLabel = "Tag",
      formatCell = (value, column) => formatReportCell(value, column, false),
      getCellValue = (row, column) => row[column],
      sourceKey = "",
    } = {}
  ) => {
    const { rowLabel, metricColumns: groupedMetricColumns, rows: tagRows } =
      normalizeGroupedTagRows(groupedReport);

    if (!tagRows.length) return null;

    const resolvedColumns = metricColumns.length ? metricColumns : groupedMetricColumns;
    if (!resolvedColumns.length) return null;

    return tagRows.map((row, index) => {
      const tagLabel =
        row[rowLabel] || row.Tag || row.tag || row.Label || row.label || "Unknown Tag";

      return (
        <tr
          key={`${tagLabel}-${index}`}
          className="clickable-row tag-inline-row"
          onClick={() =>
            openTagDetailModal({
              reportType,
              reportTitle,
              row: { ...row, __tagLabel: tagLabel, __sourceKey: sourceKey },
              metricColumns: resolvedColumns,
            })
          }
        >
          <td className="metric-title">
            <div className="tag-cell">
              <span>{tagLabel}</span>
              <small>{firstColumnLabel}</small>
            </div>
          </td>
          {resolvedColumns.map((column) => (
            <td
              key={column}
              className={getGrowthTextClass(getCellValue(row, column), column)}
            >
              {formatCell(getCellValue(row, column), column)}
            </td>
          ))}
        </tr>
      );
    });
  };

  const renderTagRowShimmer = (columns = []) => {
    return Array.from({ length: 4 }).map((_, index) => (
      <tr key={`tag-shimmer-${index}`} className="tag-inline-row shimmer-row">
        <td className="metric-title">
          <div className="tag-cell">
            <ShimmerBlock height={12} width="60px" />
            <small>
              <ShimmerBlock height={8} width="40px" />
            </small>
          </div>
        </td>

        {columns.map((col, i) => (
          <td key={i}>
            <ShimmerBlock height={12} width="50px" />
          </td>
        ))}
      </tr>
    ));
  };

  const renderTableContent = (
    reportData,
    { reportType = "dashboard", reportTitle = "Tag Volume" } = {}
  ) => {
    if (!reportData?.table) return null;

    const { value = {}, volume = {} } = reportData.table;
    const columns = Object.keys(value || {});

    return (
      <div className="report-table-wrapper">
        <table className="report-table">
          <thead>
            <tr>
              <th>Metric</th>
              {columns.map((col) => (
                <th key={col}>{formatSalesReportColumnLabel(col)}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            <tr>
              <td className="metric-title">Value</td>
              {columns.map((key) => {
                const val = value?.[key];
                return (
                  <td
                    key={key}
                    className={getGrowthTextClass(val, key)}
                  >
                    {formatReportCell(val, key, true)}
                  </td>
                );
              })}
            </tr>

            <tr>
              <td className="metric-title">Volume</td>
              {columns.map((key) => {
                const val = volume?.[key];
                return (
                  <td
                    key={key}
                    className={getGrowthTextClass(val, key)}
                  >
                    {formatReportCell(val, key, false)}
                  </td>
                );
              })}
            </tr>

            {renderInlineTagRows(tagGroupedReports[reportType], {
              reportType,
              reportTitle,
              metricColumns: columns,
              firstColumnLabel: "Tag Volume",
              formatCell: (value, column) => formatReportCell(value, column, false),
            })}

            {!tagGroupedReports[reportType] && loadingCoreTagRows
              ? renderTagRowShimmer(columns)
              : null}
          </tbody>
        </table>
      </div>
    );
  };

  const renderActivationYtdMatrixTable = (table, { isCurrency = false, tone = "value" } = {}) => {
    if (!table?.columns || !Array.isArray(table?.rows)) return null;

    const columns = table.columns;
    const rows = table.rows;

    return (
      <div className={`ytd-matrix-block ytd-matrix-block--${tone}`}>
        <div className="ytd-matrix-title">{table.title}</div>
        <div className="report-table-wrapper ytd-matrix-scroll">
          <table className="report-table ytd-matrix-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={column.key === "category" ? "sticky-col" : ""}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.length ? (
                rows.map((row) => {
                  const isTotal = String(row.category || "").toLowerCase() === "total";

                  return (
                    <tr key={row.category} className={isTotal ? "ytd-matrix-total-row" : ""}>
                      {columns.map((column) => {
                        const rawValue = row[column.key];
                        const isGrowthMetric = column.type === "growth";
                        const content =
                          column.type === "text"
                            ? rawValue || "-"
                            : isGrowthMetric
                            ? formatPercent(rawValue)
                            : formatReportCell(rawValue, column.key, isCurrency);

                        const className = [
                          column.key === "category" ? "sticky-col metric-title" : "",
                          isGrowthMetric ? getGrowthTextClass(rawValue) : "",
                        ]
                          .filter(Boolean)
                          .join(" ");

                        return (
                          <td key={column.key} className={className}>
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className="ytd-matrix-empty">
                    No activation YTD data for the selected filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderActivationYtdMatrixContent = (report) => {
    if (!report) return null;

    return (
      <div className="ytd-matrix-section">
        {renderActivationYtdMatrixTable(report.sellOutValue, {
          isCurrency: true,
          tone: "value",
        })}
        {renderActivationYtdMatrixTable(report.sellOutVolume, {
          isCurrency: false,
          tone: "volume",
        })}
      </div>
    );
  };

  const renderTagAwareReportContent = (
    reportData,
    { reportType, reportTitle, renderPrimary } = {}
  ) => renderPrimary?.(reportData, { reportType, reportTitle }) || null;

  const isNumeric = (val) => {
    return val !== null && val !== undefined && val !== "" && !Number.isNaN(Number(val));
  };

  const getComparableColumns = (columns) => {
    return columns.filter(
      (col) =>
        col !== "G/D%" &&
        !isExpAchievementColumn(col) &&
        !isExpectedAchievementColumn(col)
    );
  };

  const getRowScaleStats = (rowData, columns) => {
    const comparableCols = getComparableColumns(columns);

    const values = comparableCols
      .map((col) => Number(rowData?.[col]))
      .filter((v) => !Number.isNaN(v));

    if (!values.length) return { min: 0, max: 0 };

    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  };

  const getNeutralIntensityStyle = (value, stats) => {
    if (!isNumeric(value)) return {};

    const num = Number(value);
    const { min, max } = stats;

    if (max === min) {
      return {
        background: "#fbfcfe",
        fontWeight: 500,
      };
    }

    const ratio = (num - min) / (max - min);
    const alpha = 0.03 + ratio * 0.08;

    return {
      background: `linear-gradient(180deg, rgba(99, 102, 241, ${alpha}) 0%, rgba(99, 102, 241, ${alpha * 0.75}) 100%)`,
      boxShadow: ratio > 0.55 ? "inset 0 0 0 1px rgba(99,102,241,0.08)" : "none",
      fontWeight: ratio > 0.7 ? 600 : 500,
      color: "#374151",
    };
  };

  const getGrowthStyle = (value) => {
    if (!isNumeric(value)) return {};

    const num = Number(value);

    if (num > 0) {
      return {
        background: "rgba(34, 197, 94, 0.08)",
        color: "#166534",
        fontWeight: 700,
      };
    }

    if (num < 0) {
      return {
        background: "rgba(239, 68, 68, 0.08)",
        color: "#b42318",
        fontWeight: 700,
      };
    }

    return {
      background: "#f8fafc",
      color: "#475467",
      fontWeight: 600,
    };
  };

  const getExpAchStyle = () => {
    return {
      background: "#fcfcfd",
      color: "#667085",
      fontWeight: 500,
    };
  };

  const getCellStyle = (value, col, rowData, columns) => {
    if (!isNumeric(value)) return {};

    if (col === "G/D%") return getGrowthStyle(value);
    if (isExpAchievementColumn(col) || isExpectedAchievementColumn(col)) return getExpAchStyle();

    const stats = getRowScaleStats(rowData, columns);
    return getNeutralIntensityStyle(value, stats);
  };

  const renderWodRow = (label, rowData, columns, rowKey) => {
    return (
      <tr key={rowKey}>
        <td className="metric-title sticky-col">{label}</td>
        {columns.map((col) => (
          <td
            key={col}
            className={`wod-value-cell ${col === "G/D%" ? "is-growth" : ""} ${
              isExpAchievementColumn(col) || isExpectedAchievementColumn(col) ? "is-exp" : ""
            }`}
            style={getCellStyle(rowData?.[col], col, rowData, columns)}
          >
            {formatReportCell(rowData?.[col], col, false)}
          </td>
        ))}
      </tr>
    );
  };

  const renderWodTablesContent = (
    reportData = wodTables,
    { reportType = "wod" } = {}
  ) => {
    if (!reportData) return null;

    const sellIn = reportData.sellInWOD || {};
    const sellOut = reportData.sellOutWOD || {};
    const columns = Object.keys(sellIn);
    const groupedWod = tagGroupedReports[reportType] || {};

    return (
      <div className="wod-section">
        <div className="sub-report-block">
          <div className="sub-report-heading">WOD Summary</div>
          <div className="report-table-wrapper">
            <table className="report-table wod-report-table">
              <thead>
                <tr>
                  <th className="sticky-col">Metric</th>
                  {columns.map((col) => (
                    <th key={col}>{formatWodColumnLabel(col)}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {renderWodRow("Sell-In WOD", sellIn, columns, "sell-in")}
                {renderInlineTagRows(groupedWod.sellInWOD, {
                  reportType,
                  reportTitle: "Sell-In WOD",
                  metricColumns: columns,
                  firstColumnLabel: "Tag WOD",
                  sourceKey: "sellInWOD",
                  getCellValue: getWodTagCellValue,
                  formatCell: (value, column) => formatReportCell(value, column, false),
                })}
                {!groupedWod.sellInWOD && loadingCoreTagRows
                  ? renderTagRowShimmer(columns)
                  : null}
                {renderWodRow("Sell-Out WOD", sellOut, columns, "sell-out")}
                {renderInlineTagRows(groupedWod.sellOutWOD, {
                  reportType,
                  reportTitle: "Sell-Out WOD",
                  metricColumns: columns,
                  firstColumnLabel: "Tag WOD",
                  sourceKey: "sellOutWOD",
                  getCellValue: getWodTagCellValue,
                  formatCell: (value, column) => formatReportCell(value, column, false),
                })}
                {!groupedWod.sellOutWOD && loadingCoreTagRows
                  ? renderTagRowShimmer(columns)
                  : null}
              </tbody>
            </table>
          </div>
        </div>

        {reportData.sellInBreakdown?.length > 0 && (
          <div className="sub-report-block">
            <div className="sub-report-heading">Sell-In WOD Breakdown</div>
            <div className="report-table-wrapper">
              <table className="report-table wod-report-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Metric</th>
                    {columns.map((col) => (
                      <th key={col}>{formatWodColumnLabel(col)}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {reportData.sellInBreakdown.map((row, idx) =>
                    renderWodRow(row.label, row.data || {}, columns, `sellin-breakdown-${idx}`)
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {reportData.sellOutBreakdown?.length > 0 && (
          <div className="sub-report-block">
            <div className="sub-report-heading">Sell-Out WOD Breakdown</div>
            <div className="report-table-wrapper">
              <table className="report-table wod-report-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Metric</th>
                    {columns.map((col) => (
                      <th key={col}>{formatWodColumnLabel(col)}</th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {reportData.sellOutBreakdown.map((row, idx) =>
                    renderWodRow(row.label, row.data || {}, columns, `sellout-breakdown-${idx}`)
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStockWodContent = () => {
    const columns = stockWod?.columns || [];
    const rows = stockWod?.rows || [];
    if (!columns.length) return null;

    return (
      <div className="report-table-wrapper">
        <table className="report-table wod-report-table">
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={column} className={index === 0 ? "sticky-col" : ""}>
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => {
              const isTotal = row.Category === "Total";
              return (
                <tr
                  key={`${row.Category || "row"}-${rowIndex}`}
                  className={isTotal ? "total-row" : "clickable-row"}
                  onClick={
                    isTotal
                      ? undefined
                      : () =>
                          openTagDetailModal({
                            reportType: "stock_wod",
                            reportTitle: "Stock WOD",
                            row: { ...row, __tagLabel: row.Category },
                            metricColumns: [
                              "SPD Stk WOD",
                              "MDD Stk WOD",
                              "Ret Stk WOD",
                              "Total Stock WOD",
                              "Total Stock Qty",
                              "Average Stock",
                            ],
                          })
                  }
                >
                  {columns.map((column, columnIndex) => {
                    const value = row[column];
                    const isWodCount = column.includes("WOD");
                    const isPlain = isWodCount || column === "Days of Stock" || column === "Stock Depth";
                    return (
                      <td
                        key={column}
                        className={columnIndex === 0 ? "metric-title sticky-col" : ""}
                      >
                        {columnIndex === 0
                          ? value
                          : isPlain
                          ? formatPlainRounded(value)
                          : formatReportCell(value, column, false)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="sales-report-page">
      <div className="report-container">
        <div className="report-header">
          <div>
            <h2>📊 Sales Dashboard</h2>
            <p className="report-subtitle">
              Extraction-style filters now apply across every report card on this page.
            </p>
          </div>

          <div className="controls">
            <button
              type="button"
              className="apply-action"
              onClick={() => {
                fetchCoreReports();
                fetchYtdReports();
                fetchPriceSegmentReports();
              }}
            >
              <FaCheck />
              Apply
            </button>

            <button type="button" className="ghost-action" onClick={resetAllFilters}>
              <FaSyncAlt />
              Reset
            </button>

            <button
              type="button"
              className="primary-action"
              onClick={() => setFilterPanelOpen((prev) => !prev)}
            >
              <FaFilter />
              Filters
              {totalSelectedFiltersCount > 0 && (
                <span className="filter-badge">{totalSelectedFiltersCount}</span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setCompactMode(!compactMode)}
            >
              {compactMode ? "Switch to Normal View" : "Switch to Cr/K View"}
            </button>
          </div>
        </div>

        <div className="sales-filter-bar">
          <div className="sales-filter-grid">
            <div className="sales-filter-field sales-date-range">
              <label>Date Range</label>
              <div className="sales-date-range__box">
                <div className="sales-date-range__inputs">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    aria-label="Start date"
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                    aria-label="End date"
                  />
                </div>
                <div className="sales-date-range__presets">
                  <button type="button" onClick={() => applyDatePreset("today")}>
                    Today
                  </button>
                  <button type="button" onClick={() => applyDatePreset("yesterday")}>
                    Yesterday
                  </button>
                  <button type="button" onClick={() => applyDatePreset("this_month")}>
                    MTD
                  </button>
                  <button type="button" onClick={() => applyDatePreset("last_month")}>
                    Last Month
                  </button>
                </div>
              </div>
            </div>

            <div className="sales-filter-field">
              <label>Brand</label>
              <input
                type="text"
                placeholder="Optional"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
              />
            </div>

            <div className="sales-filter-field">
              <label>Segment</label>
              <input
                type="text"
                placeholder="Optional"
                value={segment}
                onChange={(e) => setSegment(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="sales-active-filters">
          <div className="sales-active-filters__title">Active Filters</div>
          <div className="sales-active-filters__content">
            {brand && (
              <FilterChip onClick={() => setBrand("")}>Brand: {brand}</FilterChip>
            )}
            {segment && (
              <FilterChip onClick={() => setSegment("")}>Segment: {segment}</FilterChip>
            )}
            {Object.keys(selectedActorFilters).map((type) => renderChips(type))}
            {Object.keys(selectedDealerFilters).map((type) => renderChips(type))}
            {!brand && !segment && totalSelectedFiltersCount === 0 && (
              <div className="sales-active-filters__empty">No extra filters selected</div>
            )}
          </div>
        </div>

        {filterPanelOpen && (
          <div className="sales-filter-overlay">
            <div className="sales-filter-panel" ref={panelRef}>
              <div className="sales-filter-panel__header">
                <div>
                  <h3>Advanced Filters</h3>
                  <p>Actor and dealer filters work together across all reports.</p>
                </div>
                <button
                  type="button"
                  className="sales-filter-panel__icon"
                  onClick={() => setFilterPanelOpen(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="sales-filter-panel__body">
                <div className="sales-filter-sidebar">
                  {actorPositions.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      className={activeFilterTab === item.value ? "active" : ""}
                      onClick={() => {
                        setActiveFilterTab(item.value);
                        setSearchText("");
                      }}
                    >
                      {item.label}
                      {(selectedActorFilters[item.value] || []).length > 0 && (
                        <span>{(selectedActorFilters[item.value] || []).length}</span>
                      )}
                    </button>
                  ))}

                  {DEALER_FILTER_TYPES.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      className={activeFilterTab === item.key ? "active" : ""}
                      onClick={() => {
                        setActiveFilterTab(item.key);
                        setSearchText("");
                      }}
                    >
                      {item.label}
                      {(selectedDealerFilters[item.key] || []).length > 0 && (
                        <span>{(selectedDealerFilters[item.key] || []).length}</span>
                      )}
                    </button>
                  ))}
                </div>

                <div className="sales-filter-content">
                  <div className="sales-filter-content__top">
                    <div>
                      <h4>
                        {actorPositions.find((p) => p.value === activeFilterTab)?.label ||
                          DEALER_FILTER_TYPES.find((d) => d.key === activeFilterTab)?.label ||
                          "Filters"}
                      </h4>
                      <small>
                        Actor filters drill down automatically as you move deeper in the hierarchy
                      </small>
                    </div>

                    <div className="sales-filter-content__actions">
                      <input
                        type="text"
                        placeholder="Search name, code or value"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                      <button type="button" className="ghost-action" onClick={clearCurrentTab}>
                        Clear
                      </button>
                    </div>
                  </div>

                  {renderChips(activeFilterTab)}

                  {loadingFilterOptions ? (
                    <OptionShimmerGrid />
                  ) : (
                    <div className="sales-option-grid">
                      {filteredCurrentOptions.length > 0 ? (
                        filteredCurrentOptions.map((item) => {
                          const isSelected = currentTabSelected.some((selected) =>
                            isActorTab(activeFilterTab)
                              ? selected.code === item.code
                              : selected.value === item.value
                          );

                          return (
                            <button
                              type="button"
                              key={item.code || `${activeFilterTab}-${String(item.value)}`}
                              className={`sales-option-pill ${isSelected ? "selected" : ""}`}
                              onClick={() => toggleSelection(activeFilterTab, item)}
                            >
                              <span>{item.label || item.name || item.value}</span>
                              {isActorTab(activeFilterTab) && item.code && (
                                <small>{item.code}</small>
                              )}
                            </button>
                          );
                        })
                      ) : (
                        <div className="sales-option-empty">No options found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <ReportGroup
          title="Core Sales Reports"
          subtitle="Primary value and volume summaries"
          tone="blue"
          defaultOpen={true}
        >
          {loadingActivation ? (
            <SectionLoader title="Activation (Sell-Out)" tone="blue" />
          ) : (
            <ReportCard
              title="Activation (Sell-Out)"
              subtitle="Sell-out value and volume summary"
              tone="blue"
            >
              {renderTagAwareReportContent(activation, {
                reportType: "activation",
                reportTitle: "Activation (Sell-Out)",
                renderPrimary: renderTableContent,
              })}
            </ReportCard>
          )}

          {loadingTertiary ? (
            <SectionLoader title="Tertiary (Sell-In)" tone="blue" />
          ) : (
            <ReportCard
              title="Tertiary (Sell-In)"
              subtitle="Sell-in value and volume summary"
              tone="blue"
            >
              {renderTagAwareReportContent(tertiary, {
                reportType: "tertiary",
                reportTitle: "Tertiary (Sell-In)",
                renderPrimary: renderTableContent,
              })}
            </ReportCard>
          )}

          {loadingSecondary ? (
            <SectionLoader title="Secondary (SPD → MDD)" tone="blue" />
          ) : (
            <ReportCard
              title="Secondary (SPD → MDD)"
              subtitle="Movement summary across channel"
              tone="blue"
            >
              {renderTagAwareReportContent(secondary, {
                reportType: "secondary",
                reportTitle: "Secondary (SPD → MDD)",
                renderPrimary: renderTableContent,
              })}
            </ReportCard>
          )}
        </ReportGroup>

        <ReportGroup
          title="WOD Reports"
          subtitle="Width of distribution insights"
          tone="purple"
          defaultOpen={true}
        >
          {loadingWod ? (
            <SectionLoader title="WOD" tone="purple" />
          ) : (
            <ReportCard
              title="WOD"
              subtitle="Sell-in and sell-out WOD overview"
              tone="purple"
            >
              {renderTagAwareReportContent(wodTables, {
                reportType: "wod",
                reportTitle: "WOD",
                renderPrimary: renderWodTablesContent,
              })}
            </ReportCard>
          )}

          {loadingStockWod ? (
            <SectionLoader title="Stock WOD" tone="purple" />
          ) : (
            stockWod && (
              <ReportCard
                title="Stock WOD"
                subtitle="Stock-point width, quantity, and depth by product category"
                tone="purple"
              >
                {renderStockWodContent()}
              </ReportCard>
            )
          )}
        </ReportGroup>

        <ReportGroup
          title="Price Segment Reports"
          subtitle="Price-wise performance breakdown"
          tone="orange"
          defaultOpen={false}
        >
          {loadingPriceSegment ? (
            <SectionLoader title="Activation – Price Segment Wise" tone="orange" />
          ) : (
            priceSegmentTables && (
              <ReportCard
                title="Activation – Price Segment Wise"
                subtitle="Segment-based activation distribution"
                tone="orange"
              >
                <PriceSegmentTable
                  data={priceSegmentTables}
                  title=""
                  formatValue={formatValue}
                />
              </ReportCard>
            )
          )}

          {loadingPriceSegmentSplit40k ? (
            <SectionLoader title="Activation – 40K vs >40K" tone="orange" />
          ) : (
            priceSegmentSplit40k && (
              <ReportCard
                title="Activation – 40K vs >40K"
                subtitle="Premium split analysis"
                tone="orange"
              >
                <PriceSegmentTable
                  data={priceSegmentSplit40k}
                  title=""
                  formatValue={formatValue}
                />
              </ReportCard>
            )
          )}
        </ReportGroup>

        <ReportGroup
          title="Activation YTD New Format"
          subtitle="Excel-style sell-out value and volume by category"
          tone="teal"
          defaultOpen={true}
        >
          {loadingActivationYtdMatrix ? (
            <SectionLoader title="Activation YTD New Format" tone="teal" />
          ) : (
            <ReportCard
              title="Activation YTD New Format"
              subtitle="Preview format for YTD dashboard reporting"
              tone="teal"
            >
              {renderActivationYtdMatrixContent(activationYtdMatrix)}
            </ReportCard>
          )}
        </ReportGroup>

        {detailModal.open && (
          <div className="sales-report-modal-overlay" onClick={closeDetailModal}>
            <div
              className="sales-report-modal"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="sales-report-modal__header">
                <div>
                  <h3>{detailModal.title}</h3>
                  <p>{detailModal.subtitle}</p>
                </div>
                <div className="sales-report-modal__actions">
                  {!detailModal.loading && !detailModal.error && detailModal.rows.length > 0 && (
                    <button
                      type="button"
                      title="Download CSV"
                      aria-label="Download CSV"
                      onClick={downloadDetailModalCsv}
                    >
                      <FaDownload />
                    </button>
                  )}
                  <button type="button" title="Close" aria-label="Close" onClick={closeDetailModal}>
                    <FaTimes />
                  </button>
                </div>
              </div>

              <div className="sales-report-modal__body">
                {detailModal.loading && (
                  <div className="sales-report-modal__state">Loading product details...</div>
                )}

                {!detailModal.loading && detailModal.error && (
                  <div className="sales-report-modal__state">{detailModal.error}</div>
                )}

                {!detailModal.loading && !detailModal.error && (
                  <div className="report-table-wrapper">
                    <table className="report-table report-table--modal">
                      <thead>
                        <tr>
                          <th>Product</th>
                          {detailModal.columns.map((column) => (
                            <th key={column}>{formatSalesReportColumnLabel(column)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detailModal.rows.map((row, index) => (
                          <tr key={`${row.modelCode}-${index}`}>
                            <td className="metric-title">
                              <div className="product-cell">
                                <span>{row.productLabel || "-"}</span>
                                <small>{row.modelCode || "-"}</small>
                              </div>
                            </td>
                            {detailModal.columns.map((column) => (
                              <td
                                key={column}
                                className={getGrowthTextClass(row[column], column)}
                              >
                                {formatDetailCell(row[column], column)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SalesReportV2;
